// TeamComponents.jsx
import React, { useState, useEffect } from 'react';
import { Reply, Smile } from 'lucide-react';
import { formatResponse } from '../../utils/questionUtils';

// Define reactions as a simple array of emoji characters
export const REACTION_TYPES = {
    THUMBS_UP: { emoji: '👍', id: '1' },
    HEART: { emoji: '❤️', id: '2' },
    SMILE: { emoji: '😊', id: '3' },
    QUESTION: { emoji: '❓', id: '4' },
    WARNING: { emoji: '⚠️', id: '5' },
    FLAG: { emoji: '🚩', id: '6' }
};


export const Comment = ({ comment, teamMembers, onReaction, reactions = {}, currentUser }) => {
    const [showReactionPicker, setShowReactionPicker] = useState(false);
    const [localReactions, setLocalReactions] = useState({});

    // Safety check for comment
    if (!comment || !teamMembers) return null;

    const user = teamMembers.find(member => member.user_id === comment.user_id);
    if (!user) return null;

    useEffect(() => {
        // Ensure reactions is handled safely
        const reactionArray = Array.isArray(reactions) ? reactions :
            Array.isArray(reactions[comment.id]) ? reactions[comment.id] : [];

        // Transform reactions array into grouped format
        const groupedReactions = reactionArray.reduce((acc, reaction) => {
            const emojiKey = reaction.emoji || reaction;
            if (!acc[emojiKey]) {
                acc[emojiKey] = [];
            }
            if (reaction.user_id) {
                acc[emojiKey].push(reaction.user_id);
            }
            return acc;
        }, {});

        console.log('Processed reactions:', {
            commentId: comment.id,
            original: reactions,
            processed: groupedReactions
        });

        setLocalReactions(groupedReactions);
    }, [reactions, comment.id]);

    const handleReaction = async (reaction) => {
        console.log('Handling reaction:', reaction);
        try {
            await onReaction(comment.id, reaction.emoji); // Pass the actual emoji character
        } catch (error) {
            console.error('Error handling reaction:', error);
        }
        setShowReactionPicker(false);
    };

    const hasUserReacted = (emoji) => {
        return localReactions[emoji]?.includes(currentUser?.id) || false;
    };

    const getReactionCount = (emoji) => {
        return localReactions[emoji]?.length || 0;
    };

    return (
        <div className="group relative">
            <div className="flex gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white 
                    ${comment.user_id.startsWith('G') ? 'bg-green-500' :
                        comment.user_id.startsWith('B') ? 'bg-blue-500' :
                            comment.user_id.startsWith('R') ? 'bg-red-500' : 'bg-gray-500'}`}
                >
                    {user.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                    <div className="flex items-baseline gap-2">
                        <span className="font-medium">{user.name}</span>
                        <span className="text-xs text-gray-500">
                            {new Date(comment.created_at).toLocaleString()}
                        </span>
                    </div>
                    <p className="mt-1 text-gray-700 whitespace-pre-wrap">{comment.comment_text}</p>

                    <div className="mt-2 flex flex-wrap items-center gap-2">
                        {/* Show existing reactions */}
                        {Object.values(REACTION_TYPES).map((reaction) => {
                            const count = getReactionCount(reaction.emoji);
                            const hasReacted = hasUserReacted(reaction.emoji);

                            if (count > 0) {
                                return (
                                    <button
                                        key={reaction.id}
                                        onClick={() => handleReaction(reaction)}
                                        className={`inline-flex items-center gap-1 px-2 py-1 text-sm rounded-full
                            ${hasReacted ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}
                            hover:bg-blue-100 transition-colors`}
                                    >
                                        {reaction.emoji} {count}
                                    </button>
                                );
                            }
                            return null;
                        })}

                        {/* Reaction picker */}
                        {showReactionPicker && (
                            <div className="absolute bottom-full left-0 mb-2 p-2 bg-white rounded-lg shadow-lg border border-gray-200 flex gap-1 z-10">
                                {Object.values(REACTION_TYPES).map((reaction) => (
                                    <button
                                        key={reaction.id}
                                        onClick={() => handleReaction(reaction)}
                                        className="text-lg hover:scale-125 transition-transform p-1"
                                    >
                                        {reaction.emoji}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const TeamResponseCard = ({ response, onSetOfficial, question, variant = 'team' }) => {
    if (!response) {
        return null;
    }

    const displayName = response.userName || response.user_name || response.name || 'Unknown User';

    const formattedResponse = formatResponse(question, {
        response_value: response.response_value,
        question_type: response.question_type,
        question_options: response.question_options
    });

    const getStyles = () => {
        switch (variant) {
            case 'official':
                return {
                    container: 'bg-blue-50 border-blue-200',
                    avatar: 'bg-green-500',
                    badge: 'bg-blue-100 text-blue-800'
                };
            case 'user':
                return {
                    container: 'bg-gray-50 border-gray-200',
                    avatar: 'bg-green-500',
                    badge: 'bg-gray-100 text-gray-800'
                };
            default:
                return {
                    container: 'bg-white border-gray-200 hover:bg-gray-50',
                    avatar: 'bg-green-500',
                    badge: 'bg-gray-100 text-gray-700'
                };
        }
    };

    const styles = getStyles();

    return (
        <div className={`flex items-start gap-3 p-4 rounded-lg border ${styles.container}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white ${styles.avatar}`}>
                {displayName.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                            {response.isCurrentUser ? 'Your response' : displayName}
                        </span>
                        {response.isOfficialResponse && (
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${styles.badge}`}>
                                Official Response
                            </span>
                        )}
                    </div>
                    {!response.isOfficialResponse && onSetOfficial && (
                        <button
                            onClick={() => onSetOfficial(response.user_id)}
                            className="text-sm text-blue-600 hover:text-blue-800"
                        >
                            Set as Official Response
                        </button>
                    )}
                </div>

                <div className="mt-2 text-sm text-gray-900">
                    {formattedResponse}
                </div>

                <div className="mt-1 text-xs text-gray-500">
                    Last updated: {new Date(response.updated_at || response.created_at).toLocaleString()}
                </div>
            </div>
        </div>
    );
};